import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, getDocs, doc, updateDoc, onSnapshot
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, TextField, Button,
  MenuItem, CircularProgress, Alert, Chip
} from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';

export default function CastNConnect() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const companyId = userProfile?.companyId || currentUser?.companyId || '';

  const [token, setToken] = useState('');
  const [display, setDisplay] = useState(null);
  const [displayId, setDisplayId] = useState(null);
  const [casts, setCasts] = useState([]);
  const [selectedCastId, setSelectedCastId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load casts for company
  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'casts'), where('companyId', '==', companyId));
    const unsub = onSnapshot(q, snap => {
      setCasts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [companyId]);

  async function handleLookup() {
    setError(''); setDisplay(null); setDisplayId(null);
    if (token.length !== 6) { setError('Koden måste vara 6 tecken.'); return; }
    setLoading(true);
    try {
      const q = query(collection(db, 'displays'), where('token', '==', token.toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) { setError('Ingen skärm hittades med den koden.'); setLoading(false); return; }
      const d = snap.docs[0];
      setDisplay({ id: d.id, ...d.data() });
      setDisplayId(d.id);
      setSelectedCastId(d.data().castId || '');
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function handleConnect() {
    if (!displayId || !selectedCastId) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await updateDoc(doc(db, 'displays', displayId), {
        castId: selectedCastId,
        status: 'connected',
      });
      setSuccess('Skärmen är nu kopplad!');
      setDisplay(prev => ({ ...prev, castId: selectedCastId, status: 'connected' }));
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  const connectedCast = casts.find(c => c.id === display?.castId);

  function getStatusChip(d) {
    if (!d?.castId) return <Chip label="VÄNTAR" size="small" sx={{ bgcolor:'#FF6B00', color:'#fff', fontWeight:'bold' }} />;
    const ls = d.lastSeen?.toDate?.() || null;
    if (ls && (Date.now() - ls.getTime()) < 120000) return <Chip label="ONLINE" size="small" sx={{ bgcolor:'#4caf50', color:'#fff', fontWeight:'bold' }} />;
    return <Chip label="OFFLINE" size="small" sx={{ bgcolor:'#555', color:'#fff', fontWeight:'bold' }} />;
  }

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a1a1a', color:'white', px: 3, py: 2, display:'flex', alignItems:'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color:'#FF6B00' }}>N</span>
          <span style={{ color:'#aaa', fontWeight:400, fontSize:'0.9rem', ml: 1 }}>– Koppla skärm</span>
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color:'#aaa' }} onClick={() => navigate(-1)}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap: 2, mb: 3 }}>
            <TvIcon sx={{ color:'#FF6B00', fontSize: 32 }} />
            <Typography variant="h5" fontWeight="bold">Koppla skärm</Typography>
          </Box>
          <Typography sx={{ color:'#666', mb: 3 }}>
            Ange den 6-tecknade koden som visas på skärmen du vill koppla.
          </Typography>

          <Box sx={{ display:'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Skärmkod"
              value={token}
              onChange={e => setToken(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'').substring(0,6))}
              inputProps={{ maxLength: 6, style:{ fontFamily:'monospace', fontSize:'1.5rem', letterSpacing: 8, textAlign:'center' } }}
              placeholder="XXXXXX"
              sx={{ flex: 1 }}
            />
            <Button variant="contained" onClick={handleLookup} disabled={loading || token.length !== 6}
              sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' }, px: 3 }}>
              {loading ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Sök'}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {display && (
            <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb: 2 }}>
                <Typography fontWeight="bold" variant="h6">{display.name || 'Skärm'}</Typography>
                {getStatusChip(display)}
              </Box>
              {connectedCast && (
                <Typography sx={{ color:'#555', fontSize:'0.9rem', mb: 2 }}>
                  Nuvarande cast: <strong>{connectedCast.name}</strong>
                </Typography>
              )}

              <TextField
                select
                fullWidth
                label="Välj cast att koppla"
                value={selectedCastId}
                onChange={e => setSelectedCastId(e.target.value)}
                sx={{ mb: 2 }}
              >
                <MenuItem value=""><em>Ingen (koppla ifrån)</em></MenuItem>
                {casts.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>

              <Button fullWidth variant="contained" onClick={handleConnect}
                disabled={saving || !selectedCastId}
                sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
                {saving ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Koppla'}
              </Button>
            </Paper>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
